import { LOCAL_STORAGE_KEYS } from "../utils/constants";
import { invokeApi } from "../bl_libs/invokeApi";
import { use_e2ee } from "../utils/utils";

/**
 * Public surveys (student / teacher AI & digital learning surveys) — "Surveys" in the
 * left menu. my-dq-api: services/survey/survey_admin.svy.service.js, /v1/admin/surveys/*.
 *
 * Admins edit WORDING only. The API rebuilds every draft from the live version and takes
 * only the text fields it lists in `text_schema`, so question structure, option codes,
 * limits and rules cannot change from here. Publishing makes the draft the next version.
 */

const auth = () => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return { 'Authorization': `Bearer ${token}` };
};

const get = (path, queryParams = {}) =>
    invokeApi({ path: `/admin/surveys${path}`, method: 'GET', headers: auth(), queryParams }, use_e2ee());

const post = (path, postData) =>
    invokeApi({ path: `/admin/surveys${path}`, method: 'POST', headers: auth(), postData }, use_e2ee());

// → { surveys: [{ survey_code, audience, title, live_version, live_since, draft, versions, responses }] }
export const listSurveys = () => get('/list');

// → { live, draft (with changes), versions, text_schema, field_labels, max_text }
export const getSurvey = (survey_code) => get('/detail', { survey_code });

// { survey_code, definition } → the saved draft, with { changes }
export const saveSurveyDraft = (survey_code, definition) => post('/draft/save', { survey_code, definition });

export const discardSurveyDraft = (survey_code) => post('/draft/discard', { survey_code });

// → { version, published_at, changes }
export const publishSurveyDraft = (survey_code) => post('/draft/publish', { survey_code });

// → { token, expires_at, version }
export const previewSurveyDraft = (survey_code) => post('/draft/preview', { survey_code });

// { survey_code, version?, from?, to?, page, limit } → { total, page, limit, responses }
export const listSurveyResponses = (params) => get('/responses', params);

// { survey_code, version?, from?, to?, labels? } → { filename, csv, rows }
export const exportSurveyResponses = (params) => get('/responses/export', params);

/**
 * Where the public survey lives, for the Preview button. The survey page is served by
 * my-dq-web: REACT_APP_SURVEY_SITE_URL is its survey base, e.g.
 * https://survey.example.org (a survey subdomain) or https://app.example.org/survey.
 */
export const SURVEY_SITE_URL = (process.env.REACT_APP_SURVEY_SITE_URL || 'http://localhost:3000/survey').replace(/\/$/, '');

// survey_code → the path segment my-dq-web serves it under (src/surveys/survey.config.js).
export const SURVEY_PATH_KEY = {
    ai_literacy_student: 'student',
    ai_literacy_teacher: 'teacher',
};

export const surveyPreviewUrl = (survey_code, token) =>
    `${SURVEY_SITE_URL}/${SURVEY_PATH_KEY[survey_code] || survey_code}?preview=${encodeURIComponent(token)}`;

/** Save the CSV text the API returns as a file. */
export const downloadCsv = (filename, csv) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const fmtDateTime = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};
